import { v7 as uuidv7 } from "uuid";
import { CartItemType } from "../../services/redux/types/CartLoyaltyV2Types";
import {
  OrderCartV2Addon,
  OrderCartV2Line,
  OrderLineType,
} from "../../services/redux/types/OrderCartV2Types";
import { AddonGroupSubItemType, AddonGroupType } from "../types";
import { AddLineInput, addLineInputToCartItemType } from "./cartLineTypes";
import { requireAddonSubItemUuid, requireMenuEntityUuid } from "./entityKeys";
import { resolveAddonSubItemDisplayName } from "./menuLookup";

// Menus store some custom VATs as a fraction (0.14) and others as a percent
// (14); the calculate API expects percent units, like config.fees.taxPercentage.
function normalizeCustomTaxPercentage(value: number): number {
  return value > 0 && value < 1 ? Math.round(value * 100 * 100) / 100 : value;
}

function mapAddonGroupSubItem(
  addonGroup: AddonGroupType,
  addonGroupSubItem: AddonGroupSubItemType
): OrderCartV2Addon {
  // Wire boundary: the shared uuid only — never a numeric id under idType:'uuid'.
  const subItemId = requireAddonSubItemUuid(addonGroupSubItem);
  const addonGroupId = requireMenuEntityUuid(
    addonGroup,
    "mapAddonGroupSubItem.addonGroup"
  );

  const displayName = resolveAddonSubItemDisplayName(addonGroupSubItem.subItem);
  const addon: OrderCartV2Addon = {
    subItemId,
    addonGroupId,
    quantity: addonGroupSubItem.count ?? 1,
    unitPrice: addonGroupSubItem.price,
    ...(displayName ? { displayName } : {}),
    // Persist the AddonGroupSubItem ROW id (what /receipt/create expects in
    // addons[].id). Menu-lookup templates keep only the last-added line's
    // options per sub-item, so a rebuild cannot reliably re-derive this id —
    // without it, repeated lines of the same sub-item leaked the addon's
    // sub-item id into the receipt (EG 452s on franchise 29).
    ...(addonGroupSubItem.id != null
      ? { addonGroupSubItemId: addonGroupSubItem.id }
      : {}),
  };

  if (
    addonGroupSubItem.previousPrice > addonGroupSubItem.price &&
    addonGroupSubItem.previousPrice > 0
  ) {
    addon.unitPriceBeforeDiscount = addonGroupSubItem.previousPrice;
  }

  const customTax = addonGroupSubItem.subItem?.customVatPercentage;
  if (customTax !== undefined) {
    addon.customTaxPercentage = normalizeCustomTaxPercentage(customTax);
  }

  const nestedAddons = mapAddonsFromSubItem(
    addonGroupSubItem.subItem?.allAddonGroups
  );
  if (nestedAddons) {
    addon.nestedAddons = nestedAddons;
  }

  return addon;
}

function mapAddonsFromSubItem(
  allAddonGroups?: AddonGroupType[]
): OrderCartV2Addon[] | undefined {
  if (!allAddonGroups?.length) {
    return undefined;
  }

  const addons = allAddonGroups.flatMap((addonGroup) =>
    (addonGroup.addonGroupSubItem ?? []).map((addonGroupSubItem) =>
      mapAddonGroupSubItem(addonGroup, addonGroupSubItem)
    )
  );

  return addons.length > 0 ? addons : undefined;
}

/** Removes client-only metadata (at every nesting level) before a calculate API call. */
export function stripAddonDisplayNames(
  addons?: OrderCartV2Addon[]
): OrderCartV2Addon[] | undefined {
  return addons?.map((addon) => {
    const stripped = { ...addon };
    delete stripped.displayName;
    delete stripped.addonGroupSubItemId;

    if (stripped.nestedAddons) {
      stripped.nestedAddons = stripAddonDisplayNames(stripped.nestedAddons);
    }

    return stripped;
  });
}

/**
 * A per-addon `customTaxPercentage` overrides `config.fees.taxPercentage`, so a
 * tax-inclusive branch must send none — its prices already carry the tax.
 */
export function stripAddonCustomTaxPercentages(
  addons?: OrderCartV2Addon[]
): OrderCartV2Addon[] | undefined {
  return addons?.map((addon) => {
    const stripped = { ...addon };
    delete stripped.customTaxPercentage;

    if (stripped.nestedAddons) {
      stripped.nestedAddons = stripAddonCustomTaxPercentages(
        stripped.nestedAddons
      );
    }

    return stripped;
  });
}

export function cartItemToOrderLine(item: CartItemType): OrderLineType {
  const line: OrderLineType = {
    lineId: item.lineId,
    // Wire boundary: the shared uuid only — never a numeric id under idType:'uuid'.
    itemId: requireMenuEntityUuid(item.itemDetails, "cartItemToOrderLine.item"),
    subItemId: requireMenuEntityUuid(
      item.subItemDetails,
      "cartItemToOrderLine.subItem"
    ),
    quantity: item.count,
    unitPrice: item.subItemDetails.price,
  };

  if (
    item.priceWithoutOffer &&
    item.priceWithoutOffer > item.subItemDetails.price
  ) {
    line.unitPriceBeforeDiscount = item.priceWithoutOffer;
  }

  if (item.note) {
    line.note = item.note;
  }

  const customTax =
    item.subItemDetails.customVatPercentage ??
    item.itemDetails.customVatPercentage;
  if (customTax !== undefined) {
    line.customTaxPercentage = normalizeCustomTaxPercentage(customTax);
  }

  const addons = mapAddonsFromSubItem(item.subItemDetails.allAddonGroups);
  if (addons) {
    line.addons = addons;
  }

  return line;
}

export function addLineInputToOrderLine(
  input: AddLineInput,
  lineId?: string
): OrderCartV2Line {
  const cartItem = addLineInputToCartItemType({
    ...input,
    lineId: lineId ?? input.lineId,
  });

  return cartItemToOrderLine(cartItem);
}

export function ensureOrderLineIds(
  lines: OrderCartV2Line[]
): OrderCartV2Line[] {
  return lines.map((line) =>
    line.lineId ? line : { ...line, lineId: uuidv7() }
  );
}
